import React from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { PageLoader } from "@/components/shared";
import { PreviewContent } from "@/components/PreviewContent";
import { Disclaimer } from "@/components/Disclaimer";
import { Home, Eye, LinkIcon } from "lucide-react";

export default function PublicPreview() {
  const { token } = useParams();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["public-preview", token],
    queryFn: async () => (await api.get(`/public/preview/${token}`)).data,
    retry: false,
  });

  if (isLoading) return <div className="min-h-screen bg-background"><PageLoader /></div>;

  if (isError || !data) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <Card className="p-10 rounded-2xl text-center max-w-md" data-testid="preview-unavailable">
          <Eye className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
          <p className="font-heading text-lg font-semibold">This preview link isn't available</p>
          <p className="text-sm text-muted-foreground mt-1">It may have been turned off or regenerated by your agent. Ask them for an updated link.</p>
        </Card>
      </div>
    );
  }

  const agentName = data.agent?.name || "your agent";

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center"><Home className="h-4 w-4 text-primary-foreground" /></div>
          <span className="font-heading font-bold tracking-tight">Look Before You Leap 808</span>
        </div>
      </header>
      <div className="bg-secondary/60 border-b border-border" data-testid="public-preview-banner">
        <div className="max-w-5xl mx-auto px-4 py-2.5 flex items-center gap-2 text-sm text-muted-foreground">
          <Eye className="h-4 w-4 shrink-0" />
          <span>Read-only view shared by <strong className="text-foreground">{agentName}</strong>. Nothing here is editable.</span>
        </div>
      </div>
      <main className="max-w-5xl mx-auto p-4 sm:p-8 space-y-8">
        <PreviewContent data={data} />
        <Disclaimer>This workspace organizes preferences and agent-gathered information. It does not recommend specific homes, prices, or neighborhoods, and does not give legal, financial, or tax advice. Your agent and licensed professionals guide those decisions.</Disclaimer>
        <div className="text-center pb-4">
          <a href="https://knowbeforeyoutour808.com" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-primary hover:underline">
            <LinkIcon className="h-3.5 w-3.5" /> Visit knowbeforeyoutour808.com
          </a>
        </div>
      </main>
    </div>
  );
}
